/**
 * Validação centralizada da resposta do endpoint /auth/me.
 * Usado em App.tsx (Docker 1) e useSsoFragmentLogin.ts (Docker 2)
 * para garantir formato consistente antes de salvar no TokenService.
 */

export interface MeUser {
  user_id: number;
  user_nome: string;
  user_login: string;
  user_isadmin?: boolean;
  permissoes?: Array<{
    perm_id?: number;
    permissoes?: {
      perm_id?: number;
      perm_descricao?: string;
      perm_path?: string;
    };
  }>;
  permissionsConfig?: Array<Record<string, unknown>>;
}

export interface ValidatedMeResult {
  success: true;
  normalized: {
    data: {
      accessToken: string;
      usuario: { id: number; nome: string; login: string };
      permissions: Array<{ perm_id: number; perm_descricao: string; perm_path: string }>;
      permissionsConfig: Array<Record<string, unknown>>;
      isAdmin: boolean;
    };
  };
}

export interface ValidationError {
  success: false;
  error: string;
}

/**
 * Valida e normaliza a resposta do GET /auth/me.
 * Retorna o objeto normalizado pronto para TokenService.setUserData()
 * ou um erro descritivo se a resposta for inválida.
 */
export function validateMeResponse(
  meResponse: unknown,
  accessToken: string
): ValidatedMeResult | ValidationError {
  if (!meResponse || typeof meResponse !== 'object') {
    return { success: false, error: 'Resposta /auth/me é null ou não é objeto' };
  }

  const resp = meResponse as Record<string, unknown>;
  const user = resp.user as MeUser | undefined;

  if (!user) {
    return { success: false, error: 'Resposta /auth/me não contém campo "user"' };
  }

  if (!user.user_id || !user.user_nome) {
    return {
      success: false,
      error: `Campos obrigatórios ausentes: user_id=${user.user_id}, user_nome=${user.user_nome}`,
    };
  }

  if (!accessToken) {
    return { success: false, error: 'accessToken não fornecido' };
  }

  // Normalizar permissões (suporta múltiplos formatos do backend)
  const permissions = Array.isArray(user.permissoes)
    ? user.permissoes.map((p: any) => {
        // Formato aninhado (legado): { perm_id, permissoes: { perm_descricao, perm_path } }
        const nested = p.permissoes;
        // Formato plano (novo): { perm_id, perm_descricao, perm_path, name, path }
        const permId = p.perm_id ?? nested?.perm_id ?? 0;
        const desc =
          p.perm_descricao ??
          nested?.perm_descricao ??
          p.name ??
          '';
        const path =
          p.perm_path ??
          nested?.perm_path ??
          p.path ??
          '';
        return {
          perm_id: permId,
          perm_descricao: desc,
          perm_path: path,
        };
      })
    : [];

  return {
    success: true,
    normalized: {
      data: {
        accessToken,
        usuario: {
          id: user.user_id,
          nome: user.user_nome,
          login: user.user_login || '',
        },
        permissions,
        permissionsConfig: (user.permissionsConfig as Array<Record<string, unknown>>) || [],
        isAdmin: Boolean(user.user_isadmin),
      },
    },
  };
}
